import superAdminAxiosInstance from "../../scenes/utils/superAdminAxiosInstance";
import { API_ENDPOINTS } from "../../config/api";

class SuperAdminService {
  // ==================== Authentication ====================
  async login(email, password) {
    const response = await superAdminAxiosInstance.post(API_ENDPOINTS.SUPER_ADMIN.LOGIN, {
      email,
      password,
    });
    return response.data;
  }

  // ==================== Statistics ====================
  async getStatistics() {
    const response = await superAdminAxiosInstance.get(API_ENDPOINTS.SUPER_ADMIN.STATS);
    return response.data;
  }

  // ==================== Tenants ====================
  async getAllTenants(options = {}) {
    const response = await superAdminAxiosInstance.get(API_ENDPOINTS.SUPER_ADMIN.TENANTS.ALL, options);
    if (options.withMeta) {
      return { data: response.data, headers: response.headers };
    }
    return response.data;
  }

  async getTenantById(tenantId) {
    const response = await superAdminAxiosInstance.get(API_ENDPOINTS.SUPER_ADMIN.TENANTS.BY_ID(tenantId));
    return response.data;
  }

  async deleteTenant(tenantId) {
    const response = await superAdminAxiosInstance.delete(API_ENDPOINTS.SUPER_ADMIN.TENANTS.DELETE(tenantId));
    return response.data;
  }

  async updateTenantStatus(tenantId, active, payload = {}) {
    const response = await superAdminAxiosInstance.put(API_ENDPOINTS.SUPER_ADMIN.TENANTS.UPDATE_STATUS(tenantId), {
      active,
      ...payload,
    });
    return response.data;
  }

  // ==================== Doctors ====================
  async getAllDoctors(options = {}) {
    const response = await superAdminAxiosInstance.get(API_ENDPOINTS.SUPER_ADMIN.DOCTORS.ALL, options);
    if (options.withMeta) {
      return { data: response.data, headers: response.headers };
    }
    return response.data;
  }

  async getDoctorById(doctorId) {
    const response = await superAdminAxiosInstance.get(API_ENDPOINTS.SUPER_ADMIN.DOCTORS.BY_ID(doctorId));
    return response.data;
  }

  async deleteDoctor(doctorId) {
    const response = await superAdminAxiosInstance.delete(API_ENDPOINTS.SUPER_ADMIN.DOCTORS.DELETE(doctorId));
    return response.data;
  }

  // ==================== Patients ====================
  async getAllPatients(options = {}) {
    const response = await superAdminAxiosInstance.get(API_ENDPOINTS.SUPER_ADMIN.PATIENTS.ALL, options);
    if (options.withMeta) {
      return { data: response.data, headers: response.headers };
    }
    return response.data;
  }

  async getPatientById(patientId) {
    const response = await superAdminAxiosInstance.get(API_ENDPOINTS.SUPER_ADMIN.PATIENTS.BY_ID(patientId));
    return response.data;
  }

  async deletePatient(patientId) {
    const response = await superAdminAxiosInstance.delete(API_ENDPOINTS.SUPER_ADMIN.PATIENTS.DELETE(patientId));
    return response.data;
  }

  // ==================== Forms ====================
  async getAllForms(options = {}) {
    const response = await superAdminAxiosInstance.get(API_ENDPOINTS.SUPER_ADMIN.FORMS.ALL, options);
    if (options.withMeta) {
      return { data: response.data, headers: response.headers };
    }
    return response.data;
  }

  async deleteForm(formId) {
    const response = await superAdminAxiosInstance.delete(API_ENDPOINTS.SUPER_ADMIN.FORMS.DELETE(formId));
    return response.data;
  }

  // ==================== Form Answers ====================
  async getAllFormAnswers(options = {}) {
    const response = await superAdminAxiosInstance.get(API_ENDPOINTS.SUPER_ADMIN.FORM_ANSWERS.ALL, options);
    return { data: response.data, headers: response.headers };
  }

  async deleteFormAnswer(answerId) {
    const response = await superAdminAxiosInstance.delete(API_ENDPOINTS.SUPER_ADMIN.FORM_ANSWERS.DELETE(answerId));
    return response.data;
  }

  // ==================== Form Invitations ====================
  async getAllFormInvitations(options = {}) {
    const response = await superAdminAxiosInstance.get(API_ENDPOINTS.SUPER_ADMIN.FORM_INVITATIONS.ALL, options);
    if (options.withMeta) {
      return { data: response.data, headers: response.headers };
    }
    return response.data;
  }

  async deleteFormInvitation(invitationId) {
    const response = await superAdminAxiosInstance.delete(API_ENDPOINTS.SUPER_ADMIN.FORM_INVITATIONS.DELETE(invitationId));
    return response.data;
  }

  // ==================== Scoring Formulas ====================
  async getAllScoringFormulas(options = {}) {
    const response = await superAdminAxiosInstance.get(API_ENDPOINTS.SUPER_ADMIN.SCORING_FORMULAS.ALL, options);
    if (options.withMeta) {
      return { data: response.data, headers: response.headers };
    }
    return response.data;
  }

  async deleteScoringFormula(formulaId) {
    const response = await superAdminAxiosInstance.delete(API_ENDPOINTS.SUPER_ADMIN.SCORING_FORMULAS.DELETE(formulaId));
    return response.data;
  }

  // ==================== Files ====================
  async getAllFiles(options = {}) {
    const response = await superAdminAxiosInstance.get(API_ENDPOINTS.SUPER_ADMIN.FILES.ALL, options);
    if (options.withMeta) {
      return { data: response.data, headers: response.headers };
    }
    return response.data;
  }

  async getFileById(fileId) {
    const response = await superAdminAxiosInstance.get(API_ENDPOINTS.SUPER_ADMIN.FILES.BY_ID(fileId));
    return response.data;
  }

  async deleteFile(fileId) {
    const response = await superAdminAxiosInstance.delete(API_ENDPOINTS.SUPER_ADMIN.FILES.DELETE(fileId));
    return response.data;
  }

  // ==================== MRs ====================
  async getAllMRs() {
    const response = await superAdminAxiosInstance.get(API_ENDPOINTS.SUPER_ADMIN.MRS.ALL);
    return response.data;
  }

  async deleteMR(mrId) {
    const response = await superAdminAxiosInstance.delete(API_ENDPOINTS.SUPER_ADMIN.MRS.DELETE(mrId));
    return response.data;
  }

  // ==================== Doctor Invitations ====================
  async getAllDoctorInvitations(options = {}) {
    const response = await superAdminAxiosInstance.get(API_ENDPOINTS.SUPER_ADMIN.DOCTOR_INVITATIONS.ALL, options);
    if (options.withMeta) {
      return { data: response.data, headers: response.headers };
    }
    return response.data;
  }

  async deleteDoctorInvitation(invitationId) {
    const response = await superAdminAxiosInstance.delete(API_ENDPOINTS.SUPER_ADMIN.DOCTOR_INVITATIONS.DELETE(invitationId));
    return response.data;
  }

  // ==================== Password Resets ====================
  async getAllPasswordResets(options = {}) {
    const response = await superAdminAxiosInstance.get(API_ENDPOINTS.SUPER_ADMIN.PASSWORD_RESETS.ALL, options);
    if (options.withMeta) {
      return { data: response.data, headers: response.headers };
    }
    return response.data;
  }

  async deletePasswordReset(resetId) {
    const response = await superAdminAxiosInstance.delete(API_ENDPOINTS.SUPER_ADMIN.PASSWORD_RESETS.DELETE(resetId));
    return response.data;
  }

  // ==================== Exports ====================
  async getExportResources() {
    const response = await superAdminAxiosInstance.get(API_ENDPOINTS.SUPER_ADMIN.EXPORTS.RESOURCES);
    return response.data;
  }

  async exportExcel(payload) {
    const response = await superAdminAxiosInstance.post(
      API_ENDPOINTS.SUPER_ADMIN.EXPORTS.EXCEL,
      payload,
      { responseType: "blob" }
    );
    return response;
  }
}

export default new SuperAdminService();

